import React from "react";
import { motion } from "framer-motion";

/**
 * MethodCard — a single slide of the Parrot-method carousel.
 *
 * Mirrors iOS client-native/src/features/method-explanation/screens/
 * MethodExplanationScreen.tsx card: a "borderElevated" card with a circular
 * tinted image holder (light-blue-translucent), 160x160 contain image, a bold
 * Primary title and a medium Primary description (lineHeight 28), all centered.
 * The active card's image scale-springs in on step change (iOS emojiScale).
 */
export interface MethodCardProps {
  image: string;
  title: string;
  description: string;
  /** True for the currently-visible step — drives the image scale-in (iOS). */
  active?: boolean;
}

const MethodCard: React.FC<MethodCardProps> = ({
  image,
  title,
  description,
  active = false,
}) => {
  return (
    <div className="flex w-full shrink-0 flex-col items-center justify-center px-4">
      <div className="flex w-full max-w-[480px] flex-col items-center gap-4 rounded-2xl border border-[var(--color-grey-light)] bg-white px-6 py-8 shadow-md">
        <div
          className="flex items-center justify-center rounded-full p-4"
          style={{ backgroundColor: "var(--color-light-blue-translucent)" }}
        >
          <motion.img
            src={image}
            alt={title}
            className="object-contain"
            // iOS resets emojiScale to 0 then springs to 1 (tension 100,
            // friction 5) each time this card becomes the active step (incl.
            // the first card on mount).
            initial={{ scale: 0 }}
            animate={{ scale: active ? 1 : 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 14 }}
            style={{
              width: "clamp(120px, 40vw, 160px)",
              height: "clamp(120px, 40vw, 160px)",
            }}
          />
        </div>
        <div className="text-center text-xl font-bold text-[var(--color-text-primary)]">
          {title}
        </div>
        <div className="text-center text-lg font-medium leading-7 text-[var(--color-text-primary)]">
          {description}
        </div>
      </div>
    </div>
  );
};

export default MethodCard;
